import React, {useState, useEffect, useCallback, useRef} from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  Avatar,
  Alert,
  Collapse,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, CircularProgress,
  useMediaQuery
} from '@mui/material';
import { Visibility, VisibilityOff, PersonOutline, LockOutlined, Close as CloseIcon } from '@mui/icons-material';
import FaceRetouchingNaturalIcon from '@mui/icons-material/FaceRetouchingNatural';
import '../styles/login.scss';
import {
  bindCodeApi, loginAndRegisterApi,
  loginApi,
  loginByFireBaseToken,
  registerApi,
  resetApi,
  sendCode,
  sendCodeVerifyCaptcha, sendSMS
} from "../api/authority";
import emailNotice from '../assets/emailNotice.png'
import {useLocation, useNavigate} from 'react-router-dom'
import {setLocalStorage, getLocalStorage} from "../util/storage";
import {LoginProps} from "../types/authority";
import {generateSignature, getUserInfo} from "../util/author";
import {setCookie} from "../util/cookie";
import {
  buildCurrentRegistrationAttributionPayload,
  clearRegistrationAttribution,
  setSharedLoginState,
} from "../util/registrationAttribution";
import { useTranslation } from 'react-i18next'
import {setSnackbarOpen} from "../store/taskSlice";
import {useDispatch} from "react-redux";
import {set} from "lodash";
import { auth } from '../services/firebase';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import Divider from "@mui/material/Divider";
import {EmailIcon, GoogleIcon} from "../icons";
import {useMatomoTrackingPoint} from "../hooks/useMatomoTrackingPoint";
import {fetchMemberInfoAsync, fetchNotifyUnreadCountAsync, setSurveyDialogOpen, setPendingLoginData} from "../store/memberSlice";
import {AppDispatch} from "../store";
import {getSurveyRecords} from "../api/common";
import {useAliyunCaptcha} from "../hooks/useAliyunCaptcha";
import GroupsIcon from '@mui/icons-material/Groups';

interface FormErrors {
  username?: string;
  password?: string;
  confirmPassword?: string
  invitationCode?: string
  verificationCode?: string
  registerInvitationCode?: string,
  phone?: string
}

export default function Login(props: {changeDialogLoading?: (open: boolean) => void ,handleSetUserInfo?: (data: LoginProps | null) => void, templateTypeProps?: 'login' | 'register' | 'code' | 'reset'}) {
  const { handleSetUserInfo, templateTypeProps, changeDialogLoading } = props;
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [bindToken, setBindToken] = useState('')

  const {trackSendVerificationCodeClick, trackRegisterPopupRegisterClick, trackLoginPopupLoginClick, trackRegisterPopupLoginClick, trackLoginMethodSwitch, trackQuestionairePopupShow} = useMatomoTrackingPoint()

  const [templateType , setTemplateType] = useState<'login' | 'register' | 'code' | 'reset'>(templateTypeProps ?? 'register')

  const [loginType, setLoginType] = useState<'email' | 'phone'>('phone')

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    invitationCode: '',
    verificationCode: '',
    registerInvitationCode: '',
    phone: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState({
    username: false,
    password: false,
    confirmPassword: false,
    registerInvitationCode: false,
    invitationCode: false,
    verificationCode: false,
    phone: false
  });
  const [formError, setFormError] = useState('');
  const [showFormError, setShowFormError] = useState(false);

  const [countdown, setCountdown] = useState<number>(0);
  const [isCounting, setIsCounting] = useState<boolean>(false);
  const [sendCodeLoading, setSendCodeLoading] = useState<boolean>(false);

  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const dispatch = useDispatch<AppDispatch>();

  const publicUrl = process.env.PUBLIC_URL || '';
  const policyUrl = `${publicUrl}/policy.html`;
  const privacyUrl = `${publicUrl}/privacy.html`;

  const {
    captchaInstance,
    captchaVerified,
    captchaVerifyParam,
    isReady: captchaReady,
    initCaptcha,
    triggerCaptcha,
    resetCaptcha,
    reinitCaptcha,
  } = useAliyunCaptcha({
    elementId: 'captcha-element-send-code',
    buttonId: 'captcha-button-send-code',
    onSuccess: () => {
      // 验证成功回调，实际发送逻辑在 useEffect 中处理
      console.log('验证码验证成功');
    },
    onError: (error: any) => {
      console.error('验证码验证失败:', error);
/*      dispatch(setSnackbarOpen({
        open: true,
        message: t('captcha_verification_failed') || '验证码验证失败，请重试'
      }));*/
      setSendCodeLoading(false);
    },
    enabled: true,
  });

  const useIsLoginPage = () => {
    const location = useLocation();
    return location.pathname.includes('login');
  };

  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);

  const boardId = queryParams.get('boardId');

  const invitationCode = queryParams.get('invitationCode');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone: string): boolean => {
    const phoneRegex = /^1[3-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const performSendCode = useCallback(async (captchaParam?: string) => {
    setSendCodeLoading(true);

    try {
      const sendRes = loginType === 'email' ?
          await sendCode(formData.username, templateType, captchaParam) : await sendSMS(formData.phone, captchaParam, templateType);

      const deviceId = getLocalStorage('device_id') || '';
      const verificationType = loginType;
      const phoneEmail = loginType === 'email' ? formData.username : formData.phone;
      const verificationErrorKey = sendRes.code !== 200 || !sendRes.data ? sendRes.i18nKey : undefined;

      if (sendRes.code !== 200 || !sendRes.data) {
        dispatch(setSnackbarOpen({
          open: true,
          message: t(sendRes.i18nKey)
        }));
        setIsCounting(false);
        // 失败后重置验证码状态和已处理标记
        processedCaptchaParamRef.current = '';
        reinitCaptcha();
      } else {
        setCountdown(60);
        setIsCounting(true);
      }
      // 发送完成后重置验证码状态和已处理标记
      processedCaptchaParamRef.current = '';
      reinitCaptcha();
      trackSendVerificationCodeClick({
        send_verification_code_status: sendRes.code,
        verification_type: verificationType,
        device_id: deviceId,
        phone_email: phoneEmail,
        verification_error_key: verificationErrorKey
      });
      setSendCodeLoading(false);

    } catch (error) {
      console.error('发送验证码失败:', error);
      setSendCodeLoading(false);
      setIsCounting(false);
      // 失败后重置验证码状态和已处理标记
      processedCaptchaParamRef.current = '';
      reinitCaptcha();
    }
  }, [formData.username, formData.phone, loginType, templateType, t, dispatch, reinitCaptcha, trackSendVerificationCodeClick, setTouched, setErrors, setIsCounting, setCountdown, setSendCodeLoading]);

  // 使用 ref 保存最新的 performSendCode 函数
  const performSendCodeRef = useRef(performSendCode);
  useEffect(() => {
    performSendCodeRef.current = performSendCode;
  }, [performSendCode]);

  // 使用 ref 跟踪已处理的验证参数，避免重复调用
  const processedCaptchaParamRef = useRef<string>('');

  // 监听验证码验证成功，自动发送验证码
  useEffect(() => {
    if (captchaVerified && captchaVerifyParam && processedCaptchaParamRef.current !== captchaVerifyParam) {
      // 标记为已处理
      processedCaptchaParamRef.current = captchaVerifyParam;
      // 使用最新的函数引用
      performSendCodeRef.current(captchaVerifyParam);
    }
  }, [captchaVerified, captchaVerifyParam]);

  const sendVerificationCode = () => {
    if (!formData.username && loginType === 'email') {
      handleBlur('username');
      return;
    }

    if (!validateEmail(formData.username) && loginType === 'email') {
      setErrors(prev => ({
        ...prev,
        username: t('invalid_email_format')
      }));
      return;
    }

    if (!formData.phone && loginType === 'phone') {
      handleBlur('phone');
      return;
    }

    if (!validatePhone(formData.phone) && loginType === 'phone') {
      setErrors(prev => ({
        ...prev,
        phone: '手机格式不正确'
      }));
      return;
    }

    triggerCaptcha();
  };

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isCounting && countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(prev => prev - 1);
      }, 1000);
    } else if (countdown === 0 && isCounting) {
      setIsCounting(false);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown, isCounting]);

  const navigate = useNavigate();

  // 页面加载时检查localStorage是否有保存的登录信息
  useEffect(() => {
    const savedUser = localStorage.getItem('rememberedUser');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setFormData({
          username: userData.username || '',
          password: userData.password || '',
          confirmPassword: userData.confirmPassword || '',
          invitationCode: userData.invitationCode || '',
          verificationCode: userData.verificationCode,
          registerInvitationCode: userData.registerInvitationCode,
          phone: userData.phone || ''
        });
        setRememberMe(true);
      } catch (error) {
        console.error('Error parsing saved user data:', error);
        // 如果解析出错，清除可能已损坏的数据
        localStorage.removeItem('rememberedUser');
      }
    }
  }, []);

  // 验证表单的函数
  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = {};

    if (templateType === 'code') {
      if (!formData.invitationCode.trim()) {
        newErrors.invitationCode = t('verification_code_required');
      }
    }else {
      if (!formData.username.trim() && loginType === 'email') {
        newErrors.username = ['register', 'reset'].includes(templateType) ? t('email_required') : t('username_required');
      }

/*      if (!formData.password) {
        newErrors.password = t('password_required');
      }else {
        if (formData.password && templateType === 'register') {
          if (formData.password && (formData.password.length < 8 || formData.password.length > 16)) {
            newErrors.password = 'Password must be 8–18 characters long.'
          }
          if (!/^(?=.*[0-9])(?=.*[a-zA-Z])/.test(formData.password)) {
            newErrors.password = 'Password must include both letters and numbers.'
          }
          if (!/^[a-zA-Z0-9+=,.@_$!-]+$/.test(formData.password)) {
            newErrors.password = 'Password can only contain numbers, English letters, and symbols: + = , . @ _ $ ! -';
          }
        }
      }

      if (!formData.confirmPassword && ['register', 'reset'].includes(templateType)) {
        newErrors.confirmPassword = t('confirm_password_required');
      }

      if (formData.password && formData.confirmPassword && ['register', 'reset'].includes(templateType)) {
         if (formData.confirmPassword !== formData.password) newErrors.confirmPassword = t('passwords_do_not_match');
      }*/

      if (!formData.verificationCode && ['register', 'reset'].includes(templateType)) {
         newErrors.verificationCode = t('verification_code_cannot_be_empty');
      }

      if (!formData.phone.trim() && loginType === 'phone') {
        newErrors.phone = '请输入手机号';
      }

    }

    return newErrors;
  };

  // 当用户名或密码更改时验证
  useEffect(() => {
    if (touched.username || touched.phone || touched.password || touched.confirmPassword) {
      const validationErrors = validateForm();
      setErrors(validationErrors);

      // 如果没有错误，隐藏表单错误消息
      if (Object.keys(validationErrors).length === 0) {
        setShowFormError(false);
      }
    }
  }, [formData, touched]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // 标记字段为已触摸
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
  };

  const completeLogin = async (res: LoginProps) => {
    setLocalStorage('userInfo', JSON.stringify(res));
    setSharedLoginState(true, res.uuid);
    handleSetUserInfo && handleSetUserInfo(res)
    generateSignature(process.env.REACT_APP_SHARED_KEY || '', {
      userId: res.uuid,
      username: res.userName,
      timestamp: Date.now(),
      permission: ['read', 'write']
    }).then(signature => {
      if (signature) setCookie('s', signature);
    });
    await dispatch(fetchMemberInfoAsync())
    dispatch(fetchNotifyUnreadCountAsync())
    changeDialogLoading && changeDialogLoading(false)
    !changeDialogLoading && navigate('/dashboard/home')
  }

  const LogonHandling = async (res: LoginProps | null) => {
    if (res) {
      if (res.code) {
        if (!res.userExtend) {
          // userExtend 为空 → 不需要填问卷，直接登录
          completeLogin(res);
          return;
        }
        const surveyRes = await getSurveyRecords(1, 10, res.token);
        const hasRecords = surveyRes?.code === 200 && surveyRes.data?.items && surveyRes.data.items.length > 0;
        if (hasRecords) {
          completeLogin(res);
        } else {
          dispatch(setPendingLoginData(res));
          changeDialogLoading && changeDialogLoading(false);
          dispatch(setSurveyDialogOpen(true));
          trackQuestionairePopupShow({
            page_id: window.location.href,
            board_id: boardId || undefined,
            switch_from: boardId ? 'board' : 'home',
          });
        }
      } else {
        setBindToken(res.token)
        setTemplateType('register')
      }
    } else {
      dispatch(setSnackbarOpen({
        open: true,
        message: t('invalid_credentials')
      }));
    }
  }

  const signInWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      // 添加一些配置以改进用户体验
      provider.setCustomParameters({
        prompt: 'select_account'
      });
      
      const result = await signInWithPopup(auth, provider);

      const user = result.user;

      const idToken = await user.getIdToken();

      const local = getTimezoneInfo().offset

      setLoginLoading(true)
      const attribution = buildCurrentRegistrationAttributionPayload();
      loginByFireBaseToken(idToken, local, attribution).then(async res => {
        await LogonHandling(res)
        if (res?.code) clearRegistrationAttribution();
        setLoginLoading(false)
      })
    } catch (error: any) {
      console.error('Sign in error:', error);
      // 更详细的错误处理
      if (error.code === 'auth/popup-blocked') {
        // 处理弹窗被阻止的情况
        console.error('Popup was blocked by the browser');
      } else if (error.code === 'auth/cancelled-popup-request') {
        // 用户关闭了登录弹窗
        console.log('Sign in popup was closed');
      } else if (error.code === 'auth/configuration-not-found') {
        // 这就是你遇到的错误
        console.error('Firebase configuration not found. Please check your Firebase config.');
      } else {
        console.error('An unknown error occurred:', error.message);
      }
    }
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleRememberMeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    setRememberMe(isChecked);

    // 如果取消勾选"记住我"，则清除已保存的信息
    if (!isChecked) {
      localStorage.removeItem('rememberedUser');
    }
  };

  const [registerSuccessDialog, setRegisterSuccessDialog] = useState(false);

  const handleRegisterSuccess = () => {
    setRegisterSuccessDialog(true);
  };

  const handleRegisterSuccessClose = () => {
    setRegisterSuccessDialog(false);
  };

  const handleRegisterSuccessConfirm = (e: React.FormEvent) => {
    setRegisterSuccessDialog(false);
    setTemplateType('register');
    login(e)
  };

  const login = (e: React.FormEvent) => {
    e.preventDefault();

    // 提交前验证表单
    const validationErrors = validateForm();
    setErrors(validationErrors);

    // 标记所有字段为已触摸
    setTouched({
      username: true,
      password: true,
      confirmPassword: true,
      invitationCode: true,
      verificationCode: true,
      registerInvitationCode: true,
      phone: true
    });

    // 如果有错误，显示表单错误提示并阻止提交
    if (Object.keys(validationErrors).length > 0) {
      setFormError(t('fill_required_fields'));
      setShowFormError(true);
      return;
    }

    // 如果勾选了"记住我"，则保存登录信息到localStorage
    if (rememberMe) {
      localStorage.setItem('rememberedUser', JSON.stringify(formData));
    } else {
      localStorage.removeItem('rememberedUser');
    }

    setLoginLoading(true)
    // 这里添加登录逻辑
    const useName = loginType === 'phone' ? formData.phone : '';
    const email =      loginType === 'email' ? formData.username : '';
    const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Shanghai';
    const deviceId = getLocalStorage('device_id') || '';
    const attribution = buildCurrentRegistrationAttributionPayload();
    loginAndRegisterApi(email, useName, formData.verificationCode, formData.registerInvitationCode, timezoneName, attribution).then(async res => {
      if (res.code === 200) {
        trackLoginPopupLoginClick({
          login_status: res?.data.code,
          device_id: deviceId,
          login_error_key: undefined
        })
        await LogonHandling(res.data)
        clearRegistrationAttribution();
      }else {
        trackLoginPopupLoginClick({
          login_status: res.code.toString(),
          device_id: deviceId,
          login_error_key: res.i18nKey
        })
        dispatch(setSnackbarOpen({
          open: true,
          message: t(res?.i18nKey)
        }));
      }

      setLoginLoading(false)
    })
  }

  const getTimezoneInfo = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const offsetHours = Math.abs(offset / 60);
    const offsetMinutes = Math.abs(offset % 60);
    const offsetSign = offset <= 0 ? '+' : '-';

    return {
      name: Intl.DateTimeFormat().resolvedOptions().timeZone,
      offset: `UTC${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`,
      offsetHours: offset / 60 * -1 // 转换为标准的时区表示法
    };
  };

  const register = (e: React.FormEvent) => {
    e.preventDefault();

    // 提交前验证表单
    const validationErrors = validateForm();

    setErrors(validationErrors);

    // 标记所有字段为已触摸
    setTouched({
      username: true,
      password: true,
      confirmPassword: true,
      invitationCode: true,
      verificationCode: true,
      registerInvitationCode: true,
      phone: true
    });

    // 如果有错误，显示表单错误提示并阻止提交
    if (Object.keys(validationErrors).length > 0) {
      setFormError(t('fill_required_fields'));
      setShowFormError(true);
      return;
    }

    const local = getTimezoneInfo().offset

    setLoginLoading(true)

    if (templateType === 'reset') {
      resetApi(formData.username, formData.password, formData.verificationCode, local).then(async res => {
        if (res.code === 200) {
          setTemplateType('register')
        } else {
          dispatch(setSnackbarOpen({
            open: true,
            message: t(res.i18nKey)
          }));
        }
        setLoginLoading(false)
      })
    }else {
      trackRegisterPopupLoginClick({})
      const attribution = buildCurrentRegistrationAttributionPayload();
      registerApi(formData.username, formData.password, formData.verificationCode, local, formData.registerInvitationCode, attribution).then(async res => {
        if (res.code === 200) {
          handleRegisterSuccess()
        } else {
          dispatch(setSnackbarOpen({
            open: true,
            message: t(res.i18nKey)
          }));
        }
        trackRegisterPopupRegisterClick({register_status: res.code})
        if (res.code === 200) clearRegistrationAttribution();
        setLoginLoading(false)
      })
    }
  }

  const code = (e: React.FormEvent) => {
    e.preventDefault();

    // 提交前验证表单
    const validationErrors = validateForm();
    setErrors(validationErrors);

    // 标记所有字段为已触摸
    setTouched({
      username: true,
      password: true,
      confirmPassword: true,
      invitationCode: true,
      verificationCode: true,
      registerInvitationCode: true,
      phone: true
    });

    // 如果有错误，显示表单错误提示并阻止提交
    if (Object.keys(validationErrors).length > 0) {
      setFormError(t('fill_required_fields'));
      setShowFormError(true);
      return;
    }

    bindCodeApi(bindToken, formData.invitationCode).then(async res => {
      if (res.code === 200) {
        if (res.data.code) {
          if (!res.data.userExtend) {
            completeLogin(res.data);
            return;
          }
          const surveyRes = await getSurveyRecords(1, 10, res.data.token);
          const hasRecords = surveyRes?.code === 200 && surveyRes.data?.items && surveyRes.data.items.length > 0;
          if (hasRecords) {
            completeLogin(res.data);
          } else {
            dispatch(setPendingLoginData(res.data));
            changeDialogLoading && changeDialogLoading(false);
            dispatch(setSurveyDialogOpen(true));
            trackQuestionairePopupShow({
              page_id: window.location.href,
              board_id: boardId || undefined,
              switch_from: boardId ? 'board' : 'home',
            });
          }
        }else {
          setBindToken(res.data.token)
          setTemplateType('code')
        }
      } else {
        dispatch(setSnackbarOpen({
          open: true,
          message: t(res.i18nKey)
        }));
      }
      setLoginLoading(false)
    })

  }

  const handleSubmit = (e: React.FormEvent) => {

/*    if (templateType === 'login') login(e);
    if (['register', 'reset'].includes(templateType)) register(e);
    if (templateType === 'code') code(e);*/

    login(e);

  };

  const buttonText = useCallback(()=>{
    const text = {
      login: {text: t('login'), slogan: t('welcome_back')},
      register: {text: t('sign_up'), slogan: '欢迎来到'},
      code: {text: t('confirm'), slogan: t('enter_your_invitation_code')},
      reset: {text: t('confirm'), slogan: t('reset_password')},
    };
    return text[templateType]
  }, [templateType])

  useEffect(()=>{
    const validationErrors = validateForm();
    setErrors(validationErrors);
  }, [templateType])

  useEffect(() => {
    if (invitationCode) {
      setFormData(prev => ({
        ...prev,
        registerInvitationCode: invitationCode
      }));
    }
  }, [invitationCode]);

  // 验证手机号格式
  const isValidPhone = (phone: string) => {
    const phoneRegex = /^1[3-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

// 验证邮箱格式
  const isValidEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  return (
    <div className="login-container" style={{minHeight: useIsLoginPage() ? "100vh" : 'fit-content'}}>
      <Dialog
          open={registerSuccessDialog}
          aria-labelledby="register-success-dialog-title"
          aria-describedby="register-success-dialog-description"
      >
{/*        <DialogTitle id="register-success-dialog-title">
          {t('registration_success')}
        </DialogTitle>*/}
        <DialogContent>
          <div id="register-success-dialog-description" style={{padding: 20}}>
            {t('registration_success_message')}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRegisterSuccessConfirm} color="primary" variant="contained" autoFocus>
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      {useIsLoginPage() && <div className="login-background"></div>}
      <Box className="login-card-wrapper" sx={{
        margin: useIsLoginPage() ? "0 20px" : '0'
      }}>
        <Card className="login-card">
          <CardContent sx={{py: 1.5, px: 2, position: 'relative'}}>
            {isMobile && (
                <IconButton
                    onClick={() => changeDialogLoading?.(false)}
                    sx={{position: 'absolute', top: 0, right: 0, zIndex: 1}}
                >
                    <CloseIcon />
                </IconButton>
            )}
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2, marginTop: isMobile ? '20px' : 0}}>
              <div>
                <span className='login-title-label'>{buttonText().slogan}</span>
                <span className="login-title">{t('Krene')}</span>
              </div>
              {
                  templateType === 'code' && <Typography variant="h4" className="login-title-code-label">
                    {t('no_code')} <a
                      href='https://wj.qq.com/s2/23848544/01c0/'
                      target='_blank'>{t('join_waitlist')}</a> | <a href='https://qm.qq.com/q/cqeTdBfmyQ'
                                                                    target='_blank'>{t('社群')}</a>
                  </Typography>
              }
            </Box>

            <Collapse in={showFormError}>
              <Alert
                  severity="error"
                  sx={{mb: 2, fontSize: '0.8rem'}}
                  onClose={() => setShowFormError(false)}
              >
                {formError}
              </Alert>
            </Collapse>

            <form autoComplete="off">
              {
                  loginType === 'email' && <TextField
                      className="login-input"
                      translate='no'
                      style={{display: templateType === 'code' ? 'none' : 'block'}}
                      name="username"
                      value={formData.username}
                      onChange={handleChange}
                      onBlur={() => handleBlur('username')}
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      placeholder={['register', 'reset'].includes(templateType) ? t('enter_email') : t('enter_username')}
                      error={touched.username && Boolean(errors.username)}
                      helperText={touched.username && errors.username}
                      size="small"
                      required
                      autoComplete="off"
                      inputProps={{
                        autoComplete: "new-username"
                      }}
                  />
              }

              {
                  loginType === 'phone' && <TextField
                      className="login-input"
                      translate='no'
                      style={{display: templateType === 'code' ? 'none' : 'block'}}
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={() => handleBlur('phone')}
                      fullWidth
                      margin="normal"
                      type='tel'
                      variant="outlined"
                      placeholder='请输入手机号'
                      error={touched.phone && Boolean(errors.phone)}
                      helperText={touched.phone && errors.phone}
                      InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                              <Typography variant="body2" color="textSecondary">
                                <div style={{borderRight: "2px solid var(--c-border)", width: "33px"}}>+86</div>
                              </Typography>
                            </InputAdornment>
                        ),
                      }}
                      size="small"
                      required
                      autoComplete="off"
                      inputProps={{
                        autoComplete: "new-username"
                      }}
                  />
              }

              {
                  ['register', 'reset'].includes(templateType) && <TextField
                      className="login-input"
                      translate='no'
                      name="verificationCode"
                      type='type'
                      value={formData.verificationCode}
                      onChange={handleChange}
                      onBlur={() => handleBlur('verificationCode')}
                      fullWidth
                      margin="normal"
                      placeholder='请输入验证码'
                      variant="outlined"
                      error={touched.verificationCode && Boolean(errors.verificationCode)}
                      helperText={touched.verificationCode && errors.verificationCode}
                      InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                              <div onClick={() => {
                                if (isCounting || sendCodeLoading) return
                                sendVerificationCode()
                              }} id={ (sendCodeLoading || isCounting) ? '' : 'captcha-button-send-code'} style={{
                                width: '100px',
                                color: 'var(--c-brand)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: '48px',
                                borderLeft: '1px solid var(--c-border)',
                                borderTopRightRadius: 10,
                                borderBottomRightRadius: 10,
                                marginRight: '-13px',
                                cursor: 'pointer'
                              }}>
                                {
                                  sendCodeLoading ?
                                      <CircularProgress size={16} color="inherit"/> :
                                      <>{isCounting ? `${countdown}s` : t('send')}</>
                                }
                              </div>
                              {/*<Button
                                  id="captcha-button-send-code"
                                  variant="contained"
                                  size="medium"
                                  onClick={sendVerificationCode}
                                  disabled={isCounting || sendCodeLoading}
                                  loading={sendCodeLoading}
                                  sx={{
                                    textTransform: "none",
                                    width: '100px',
                                    minWidth: '80px',
                                    height: '48px',
                                    borderTopLeftRadius: 0,
                                    borderBottomLeftRadius: 0,
                                    borderTopRightRadius: 10,
                                    borderBottomRightRadius: 10,
                                    color: 'var(--c-brand)',
                                    backgroundColor: 'var(--c-bg)',
                                    marginRight: '-13px'
                                  }}
                              >
                                {isCounting ? `${countdown}s` : t('send')}
                              </Button>*/}
                            </InputAdornment>
                        )
                      }}
                      size="small"
                      required
                      autoComplete="off"
                      inputProps={{
                        autoComplete: "new-confirmPassword"
                      }}
                  />
              }

              {/*<TextField
                  className="login-input"
                  style={{marginBottom: '15px', display: templateType === 'code' ? 'none' : 'block'}}
                  label={t('password')}
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={() => handleBlur('password')}
                  fullWidth
                  margin="normal"
                  placeholder={t('enter_password')}
                  variant="outlined"
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlined/>
                        </InputAdornment>
                    ),
                    endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              edge="end"
                              size="small"
                          >
                            {showPassword ? <Visibility fontSize="small"/> : <VisibilityOff fontSize="small"/>}
                          </IconButton>
                        </InputAdornment>
                    )
                  }}
                  size="small"
                  required
                  autoComplete="off"
                  inputProps={{
                    autoComplete: "new-password"
                  }}
              />*/}

              {/*{
                  ['register', 'reset'].includes(templateType) && <TextField
                      className="login-input"
                      style={{marginBottom: '25px'}}
                      label={t('confirm_password')}
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      onBlur={() => handleBlur('confirmPassword')}
                      fullWidth
                      margin="normal"
                      placeholder={t('re_enter_password')}
                      variant="outlined"
                      error={touched.confirmPassword && Boolean(errors.confirmPassword)}
                      helperText={touched.confirmPassword && errors.confirmPassword}
                      InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                              <LockOutlined/>
                            </InputAdornment>
                        ),
                        endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => {
                                    setShowConfirmPassword(!showConfirmPassword)
                                  }}
                                  edge="end"
                                  size="small"
                              >
                                {showConfirmPassword ? <Visibility fontSize="small"/> : <VisibilityOff fontSize="small"/>}
                              </IconButton>
                            </InputAdornment>
                        )
                      }}
                      size="small"
                      required
                      autoComplete="off"
                      inputProps={{
                        autoComplete: "new-confirmPassword"
                      }}
                  />
              }*/}

              {
                  loginType === 'phone' && <TextField
                      className="login-input"
                      translate='no'
                      style={{marginBottom: '25px'}}
                      name="registerInvitationCode"
                      type='text'
                      value={formData.registerInvitationCode}
                      onChange={handleChange}
                      onBlur={() => handleBlur('registerInvitationCode')}
                      fullWidth
                      margin="normal"
                      placeholder='填写邀请码获更多奖励（可选）'
                      variant="outlined"
                      error={touched.registerInvitationCode && Boolean(errors.registerInvitationCode)}
                      helperText={touched.registerInvitationCode && errors.registerInvitationCode}
                      size="small"
                      autoComplete="off"
                  />
              }

              {
                  templateType === 'code' && <TextField
                      className="login-input"
                      translate='no'
                      style={{marginBottom: '25px'}}
                      label={t('invitation_code')}
                      name="invitationCode"
                      type='text'
                      value={formData.invitationCode}
                      onChange={handleChange}
                      onBlur={() => handleBlur('password')}
                      fullWidth
                      margin="normal"
                      placeholder={t('invitation_code')}
                      variant="outlined"
                      error={touched.invitationCode && Boolean(errors.invitationCode)}
                      helperText={touched.invitationCode && errors.invitationCode}
                      InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                              <FaceRetouchingNaturalIcon/>
                            </InputAdornment>
                        )
                      }}
                      size="small"
                      required
                      autoComplete="off"
                      inputProps={{
                        autoComplete: "new-invitationCode"
                      }}
                  />
              }

              <Box className="login-options" style={{display: templateType === 'login' ? 'flex' : 'none'}}>
                <FormControlLabel
                    style={{marginLeft: '-5px'}}
                    control={
                      <Checkbox
                          checked={rememberMe}
                          onChange={handleRememberMeChange}
                          color="primary"
                          size="small"
                      />
                    }
                    label={t('remember_me')}
                />
                <Typography
                    variant="body2"
                    className="forgot-password"
                >
                  <span onClick={() => {
                    setTemplateType('reset')
                  }}>{t('forget_password')}</span>
                </Typography>
              </Box>

              <div style={{
                backgroundColor: 'var(--c-bg-tertiary)',
                color: 'var(--c-text-secondary)',
                fontSize: '14px',
                borderRadius: '20px',
                height: '30px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: 10
              }}>遇到问题？
                <a href='https://qm.qq.com/q/DdeNxVg01i'>
                  <span style={{color: 'var(--c-brand)', cursor: 'pointer'}}>联系客服</span>
                </a>
              </div>

              <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  className="login-button"
                  size="small"
                  sx={{
                    fontSize: '14px',
                    textTransform: "none",
                    backgroundColor: 'var(--c-brand)',
                    borderRadius: '10px'
                  }}
                  disabled={
                    loginType === 'phone'
                        ? !isValidPhone(formData.phone) || formData.verificationCode.length < 6
                        : !isValidEmail(formData.username) || formData.verificationCode.length < 6
                  }
                  onClick={(e) => {
                    handleSubmit(e)
                  }}
                  loading={loginLoading}
              >
                {loginType === 'email' ? '登录' : '注册 / 登录'}
              </Button>

              <Divider sx={{
                display: 'flex',
                alignItems: 'center',
                my: 2,
                '&::before, &::after': {
                  content: '""',
                  flex: 1,
                  borderColor: 'var(--c-border)'
                }
              }}>
                <Typography variant="body2" sx={{px: 2, color: 'text.secondary'}}>
                  其它方式登录
                </Typography>
              </Divider>

              {
                  loginType === 'phone' && <div onClick={() => {
                  const device_id = getLocalStorage('device_id') || undefined;
                  const page_id = window.location.href;
                  trackLoginMethodSwitch({
                    device_id,
                    page_id,
                    login_switch_to: 'email'
                  })
                  setLoginType('email')
                }} style={{
                  color: 'var(--c-text-primary)',
                  border: '1px solid var(--c-text-primary)',
                  fontSize: '14px',
                  width: '116px',
                  height: '32px',
                  borderRadius: '20px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '0 auto',
                  cursor: 'pointer'
                }}>
                  <EmailIcon/> &nbsp;邮箱登录
                </div>
              }

              {
                  loginType === 'email' && <div onClick={() => {
                    const device_id = getLocalStorage('device_id') || undefined;
                    const page_id = window.location.href;
                    trackLoginMethodSwitch({
                      device_id,
                      page_id,
                      login_switch_to: 'phone'
                    })
                    setLoginType('phone')
                  }} style={{
                    color: 'var(--c-text-primary)',
                    border: '1px solid var(--c-text-primary)',
                    fontSize: '14px',
                    width: '116px',
                    height: '32px',
                    borderRadius: '20px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    margin: '0 auto',
                    cursor: 'pointer'
                  }}>
                    <EmailIcon/> &nbsp;手机登录
                  </div>
              }

              <div style={{
                textAlign: "center",
                margin: "6px 0 10px 0",
                visibility: loginType === 'phone' ? 'visible' : 'hidden'
              }}>
                <img src={emailNotice} alt=''/>
              </div>

              {/*{
                  templateType === 'login' && <Typography
                      variant="body2"
                      className="change-method"
                  >
                    <span style={{fontSize: 14}} onClick={() => {
                      setTemplateType('register')
                    }}><span style={{color: 'var(--c-text-primary)'}}>{t('dont_have_account')}&nbsp;</span> {t('sign_up')}</span>
                  </Typography>
              }*/}

              {/* {
                  ['register', 'reset'].includes(templateType) && <Typography
                      variant="body2"
                      className="change-method"
                  >
                    <span style={{fontSize: 14}} onClick={() => {
                      setTemplateType('login')
                    }}>{t('login')}</span>
                  </Typography>
              }*/}

              {/*{
                  templateType === 'code' && <Typography
                      variant="body2"
                      className="change-method"
                  >
                    <span onClick={() => {
                      setTemplateType('login')
                    }}>{t('return')}</span>
                  </Typography>
              }*/}

              {/* {
                  templateType === 'login' && <div>
                    <Divider sx={{
                      display: 'flex',
                      alignItems: 'center',
                      my: 2,
                      '&::before, &::after': {
                        content: '""',
                        flex: 1,
                        borderColor: 'rgba(0, 0, 0, 0.12)'
                      }
                    }}>
                      <Typography variant="body2" sx={{px: 2, color: 'text.secondary'}}>
                        {t('or')}
                      </Typography>
                    </Divider>
                    <div className='anotherLogin' onClick={signInWithGoogle}>
                      <GoogleIcon/> &nbsp;&nbsp;&nbsp;{t('continue_with_google')}
                    </div>
                  </div>
              }*/}

              {/*<div className="question">遇到问题？ <a href='https://qun.qq.com/universal-share/share?ac=1&authKey=f0Gy4KZbNInb85QyzRTHOLOez8og6Oq32f2h2QkEay62B6sMVBJrE5uQkhJw/0x5&busi_data=eyJncm91cENvZGUiOiI5ODA0NTUzMDAiLCJ0b2tlbiI6IllhSTNsdlNkbTRCSnRyRjlHVWFTZUVMNkJFMytvNGlKZDlXZDFLQVVreXdrdlphT0wrbE0vVEh6d0k2aVBKUWEiLCJ1aW4iOiIxMjI0MzI2ODA5In0=&data=5lo6Y09zvUJORlSijid9etIuT-vgGW6g3GrDrvVbrl2c7RIOK7ptqNWHFk3LiGPKzvgo5GcYS-nSmdgT2iqJqoYcmnw47JRzAyMtiDnBdK8&svctype=5&tempid=h5_group_info'>联系客服</a></div>*/}

              {/*              <Typography
                  variant="body2"
                  className="policy"
              >
                <a href='https://subsequent-cantaloupe-723.notion.site/Krene-Terms-of-Service-26bb68014b5280e79486f5dbbced57c4?source=copy_link'
                   target='_blank'>{t('terms_of_service')}</a> | <a
                  href='https://subsequent-cantaloupe-723.notion.site/Krene-Privacy-Policy-26bb68014b528048967bd94380079782?source=copy_link'
                  target='_blank'>{t('privacy_policy')}</a>
              </Typography>*/}

              <div style={{
                height: "40px",
                backgroundColor: "var(--c-bg-secondary)",
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                fontSize: '14px',
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                color: 'var(--c-text-secondary)'
              }}>
                注册登录即代表同意&nbsp;
                <a style={{color: 'var(--c-brand)'}}
                   href={policyUrl}
                   target='_blank'>{t('terms_of_service')}</a>&nbsp;和&nbsp;<a style={{color: 'var(--c-brand)'}}
                                                                               href={privacyUrl}
                                                                               target='_blank'>{t('privacy_policy')}</a>
              </div>
            </form>
            {/* 阿里云验证码渲染容器（隐藏） */}
            <div id="captcha-element-send-code" style={{display: 'none'}}></div>
          </CardContent>
        </Card>
      </Box>
    </div>
  );
}
